import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PropTypes from "prop-types";

const VolumeDiskDetails = ({ resourceOverview, onEditDisk }) => {
  if (!resourceOverview?.resourceVolumeDetails || resourceOverview.resourceVolumeDetails.length === 0) {
    return (
      <Box 
        sx={{ 
          mb: 4, 
          p: 3, 
          border: "1px solid", 
          borderColor: "divider", 
          borderRadius: 2, 
          bgcolor: (t) => t.palette.mode === 'dark' ? 'background.paper' : '#f5f5f5' 
        }}
      >
        <Typography variant="h6" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          No existing disk configuration found
        </Typography>
      </Box>
    );
  }

  const showActions = onEditDisk && resourceOverview.resourceVolumeDetails.some(
    (volume) => volume.isEditable || volume.isEditInProgress
  );

  const gridColumns = showActions 
    ? '1.5fr 1.5fr 1fr 1.2fr 1.2fr 1.2fr 1fr 1.2fr 1fr 30px' 
    : '1.5fr 1.5fr 1fr 1.2fr 1.2fr 1.2fr 1fr 1.2fr 1fr';

  return (
    <Box sx={{ mb: 4, overflowX: 'auto' }}>
      <Box sx={{ minWidth: 1000 }}>
        {/* Table Header */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: gridColumns, 
          gap: 2,
          alignItems: 'center',
          p: 2,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
          borderRadius: '8px 8px 0 0',
          border: '1px solid',
          borderColor: (t) => t.palette.mode === 'dark' ? 'divider' : '#e0e0e0',
          borderBottom: 'none'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Volume ID
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Name
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Volume Size
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Device Name
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Device Type
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Volume Type
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            IOPS
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Throughput
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Status
          </Typography>
          {showActions && <Box />} 
        </Box>

        {/* Table Rows */}
        {resourceOverview.resourceVolumeDetails.map((volume, index) => (
          <Box 
            key={volume.volumeId || index}
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: gridColumns, 
              gap: 2,
              alignItems: 'center',
              p: 2,
              bgcolor: (t) => index % 2 === 0 
                ? (t.palette.mode === 'dark' ? 'background.paper' : '#ffffff') 
                : (t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f9f9f9'),
              border: '1px solid',
              borderColor: (t) => t.palette.mode === 'dark' ? 'divider' : '#e0e0e0',
              borderTop: 'none',
              ...(index === resourceOverview.resourceVolumeDetails.length - 1 && {
                borderRadius: '0 0 8px 8px'
              }),
              ...(onEditDisk && volume.isEditable && !volume.isEditInProgress && {
                '&:hover': {
                  bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : '#e3f2fd'
                }
              })
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-word'}}>
              {volume.volumeId || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
              {volume.volumeName || volume.Name || volume.name || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.volumeSize ? `${volume.volumeSize} GB` : 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.deviceName || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.diskType || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.volumeType || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.iops || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.throughput ? `${volume.throughput} MiB/s` : 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {volume.displayStatus || 'N/A'}
            </Typography>

            {showActions && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {onEditDisk && (volume.isEditable || volume.isEditInProgress) ? (
                  <Tooltip title={volume.isEditInProgress ? "Update in progress" : "Edit Disk"}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEditDisk(volume)}
                        disabled={volume.isEditInProgress}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            )}
          </Box>
        ))}
       </Box>
    </Box>
  );
};

VolumeDiskDetails.propTypes = {
  resourceOverview: PropTypes.shape({
    resourceVolumeDetails: PropTypes.arrayOf(
      PropTypes.shape({
        volumeId: PropTypes.string,
        volumeName: PropTypes.string,
        name: PropTypes.string,
        volumeSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        deviceName: PropTypes.string,
        diskType: PropTypes.string,
        volumeType: PropTypes.string,
        iops: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        throughput: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        displayStatus: PropTypes.string,
        isEditable: PropTypes.bool,
        isEditInProgress: PropTypes.bool,
      })
    ),
  }),
  onEditDisk: PropTypes.func,
};

export default VolumeDiskDetails;